// SettingsManager.jsx
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Badge, Card, Spinner, Button, Table } from "react-bootstrap";
import { User, CreditCard, FileText, Download, ArrowUpCircle } from "lucide-react";
import CalendarIntegrations from "./Calendar/CalendarIntegrations";
import { userService } from "../services/userService";
import { billingService } from "../services/billingService";
import PlanSelectionModal from "./Billing/PlanSelectionModal";


const SettingsManager = ({ auth }) => {
  const token = auth?.user?.access_token;
  const userProfile = auth?.user?.profile;

  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const fetchData = async () => {
    try {
      const [sub, inv] = await Promise.all([
        userService.getSubscription(),
        billingService.getInvoices(token)
      ]);
      setSubscription(sub);
      setInvoices(inv || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (auth?.isAuthenticated) fetchData();
  }, [auth?.isAuthenticated]);

  // Determine if user can upgrade (is Free or undefined)
  // Also check if status is strictly active to hide upgrade sometimes, but upselling is always good.
  const isFree = !subscription || subscription.plan_name === 'Free' || subscription.status !== 'active';

  return (
    <div className="d-flex bg-light min-vh-100">
      <div className="container py-5">
        <h1 className="fw-bold mb-4">Settings</h1>

        <div className="row g-4">
          {/* Profile & Subscription Section */}
          <div className="col-12 col-md-4">
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-white py-3 border-bottom">
                <h5 className="mb-0 d-flex align-items-center">
                  <User size={20} className="me-2 text-primary" /> Profile
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-column align-items-center text-center mb-4">
                  {userProfile?.picture ? (
                    <img src={userProfile.picture} alt="Avatar" className="rounded-circle mb-3 border" width="80" height="80" />
                  ) : (
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mb-3" style={{ width: 80, height: 80, fontSize: '2rem' }}>
                      {userProfile?.name?.charAt(0) || "U"}
                    </div>
                  )}
                  <h5 className="fw-bold mb-1">{userProfile?.name || "User"}</h5>
                  <p className="text-muted small mb-0">{userProfile?.email}</p>
                </div>

                <hr className="my-4" />

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="d-flex align-items-center mb-0 text-uppercase small fw-bold text-muted">
                    <CreditCard size={16} className="me-2" /> Subscription
                  </h6>
                  {!loading && isFree && (
                    <Button variant="link" className="p-0 text-decoration-none small fw-bold" onClick={() => setShowUpgrade(true)}>
                      Upgrade
                    </Button>
                  )}
                </div>

                {loading ? (
                  <div className="text-center"><Spinner size="sm" /></div>
                ) : (
                  <div className="bg-light p-3 rounded text-center border">
                    <div className="text-muted small mb-1">Current Plan</div>
                    <h4 className="fw-bold text-primary mb-2">
                      {subscription?.plan_name || "Free"}
                    </h4>
                    <Badge bg={subscription?.status === 'active' ? 'success' : 'secondary'} className="mb-3">
                      {subscription?.status || "Active"}
                    </Badge>

                    {isFree && (
                      <Button className="w-100 shadow-sm d-flex align-items-center justify-content-center text-white"
                        style={{ background: 'linear-gradient(45deg, #2575fc 0%, #6a11cb 100%)', border: 'none' }}
                        onClick={() => setShowUpgrade(true)}>
                        <ArrowUpCircle size={18} className="me-2" />
                        Upgrade to Pro
                      </Button>
                    )}

                    {subscription?.current_period_end && (
                      <div className="mt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                        Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Workspace Settings moved to ProfileAndWorkspace */}
          </div>

          <div className="col-12 col-md-8">
            {/* Calendar Integrations */}
            <CalendarIntegrations />

            {/* Invoices Section */}
            <Card className="shadow-sm border-0 mt-4">
              <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center">
                  <FileText size={20} className="me-2 text-primary" /> My Invoices
                </h5>
                <Button variant="outline-secondary" size="sm" onClick={fetchData} title="Refresh">
                  Refresh
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {invoices.length > 0 ? (
                  <Table hover responsive className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Date</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th className="text-end pe-4">Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id}>
                          <td className="ps-4">{new Date(inv.created_at).toLocaleDateString()}</td>
                          <td>{inv.plan_name}</td>
                          <td>${(inv.amount_due_cents / 100).toFixed(2)}</td>
                          <td>
                            <Badge bg={inv.status === 'paid' ? 'success' : (inv.status === 'pending' ? 'warning' : 'secondary')} pill>
                              {inv.status}
                            </Badge>
                          </td>
                          <td className="text-end pe-4">
                            <Button variant="link" size="sm" className="text-muted p-0">
                              <Download size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <div className="mb-2"><FileText size={32} className="text-light-gray" /></div>
                    No invoices found.
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      <PlanSelectionModal
        show={showUpgrade}
        onHide={() => setShowUpgrade(false)}
        token={token}
        onSuccess={() => {
          fetchData();
        }}
      />
    </div>
  );
};

export default SettingsManager;
